import React, { useRef, useCallback, useState, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import { IconChevronRight, IconCamera } from '@tabler/icons';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import getValidationErrors from '../../../utils/getValidationErrors';

import AvatarModal from '../../../components/AvatarModal';
import BackHeader from '../../../components/BackHeader';
import Input from '../../../components/Form/Input';
import Button from '../../../components/Button';
import Offline from '../../../components/OfflineMessage';
import { Label, Spin } from '../../../components/Generic';
import { Container, Content, AvatarContainer, BlueLink } from './styles';

import userImg from '../../../assets/images/user-avatar.png';

interface ProfileData {
  nome: string;
  email: string;
  formacao_profissional?: string;
  facebook_nome?: string;
  instagram_nome?: string;
  whatsapp_numero?: string;
  youtube_link?: string;
  new_password: string;
  old_password: string;
}

const Profile: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const [selectedImage, setSelectedImage] = useState<any>();

  const formRef = useRef<FormHandles>(null);

  const { addToast } = useToast();
  const { user, updateUser } = useAuth();
  const history = useHistory();
  const isOnline = useOnlineStatus();

  const handleSubmit = useCallback(
    async (data: ProfileData) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          nome: Yup.string().required('Nome obrigatório.'),
          email: Yup.string()
            .required('Email obrigatório.')
            .email('Informe um email válido.'),
          whatsapp_numero: Yup.string(),
          youtube_link: Yup.string().url('Link inválido.'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api.put('/perfil/atualizar', {
          senha: '',
          senha_antiga: '',
          ...data,
        });

        updateUser({
          id: user.id,
          url: user.url,
          ...data,
        });

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Seus dados foram atualizados.',
        });

        history.goBack();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const uploadNewAvatar = async (file: string | File | Blob) => {
    try {
      setLoading(true);

      const data = new FormData();
      data.append('avatar', file);

      const response = await api.patch('/usuarios/avatar', data);

      updateUser(response.data);

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Avatar atualizado.',
      });

      history.goBack();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado!',
      });

      console.log(err);
    } finally {
      setLoading(false);
      setSelectedImage(null);
    }
  };

  return (
    <Container>
      <BackHeader title="Editar perfil" />

      {!isOnline ? (
        <Offline />
      ) : (
        <Content>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <AvatarContainer isLoading={loading}>
              <div>
                <img src={user.url || userImg} alt="Foto" />

                <label htmlFor="avatar">
                  <IconCamera size={25} />
                  <input
                    type="file"
                    id="avatar"
                    onChange={(e: any) => setSelectedImage(e.target.files[0])}
                  />
                </label>
              </div>

              {loading && <Spin />}
            </AvatarContainer>

            <AvatarModal
              selectedImage={selectedImage}
              getResult={uploadNewAvatar}
            />

            <Input
              label="Nome"
              name="nome"
              type="text"
              defaultValue={user.nome}
            />

            <Input
              label="Email"
              name="email"
              type="email"
              defaultValue={user.email}
            />

            <BlueLink onClick={() => history.push('/perfil/editar/senha')}>
              Atualizar senha
              <IconChevronRight size={16} />
            </BlueLink>

            <Label>Opcionais</Label>

            <Input
              label="Formação"
              name="formacao_profissional"
              type="text"
              defaultValue={user.formacao_profissional}
            />

            <Input
              label="WhatsApp"
              name="whatsapp_numero"
              type="tel"
              placeholder="00 00000-0000"
              defaultValue={user.whatsapp_numero}
            />

            <Input
              label="Facebook"
              name="facebook_nome"
              type="text"
              placeholder="@usuario"
              defaultValue={user.facebook_nome}
            />

            <Input
              label="Instagram"
              name="instagram_nome"
              type="text"
              placeholder="@usuario"
              defaultValue={user.instagram_nome}
            />

            <Input
              label="YouTube (URL)"
              name="youtube_link"
              type="text"
              defaultValue={user.youtube_link}
            />

            <Button type="submit" isLoading={loading} style={{ marginTop: 24 }}>
              Salvar
            </Button>
          </Form>
        </Content>
      )}
    </Container>
  );
};

export default Profile;
